<template>
  <div class="page-wrapper has-tabbar">
    <div class="purchase-container desktop-container desktop-container-with-tabbar">
      <div class="header-section">
        <div class="balance-card">
          <div class="balance-content">
            <div class="balance-label">
              <span class="balance-icon">💰</span>
              <span class="balance-text">{{ t('purchase.balance') }}</span>
            </div>
            <div class="balance-amount">{{ displayBalance }}</div>
          </div>
        </div>
      </div>

      <div class="category-section">
        <div class="category-tabs">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            class="category-tab"
            :class="{ 'tab-active': activeTab === tab.key }"
            @click="switchTab(tab.key)"
          >
            <span class="tab-text">{{ tab.label }}</span>
          </div>
        </div>
      </div>

      <div class="orders-section">
        <el-scrollbar
          ref="scrollbarRef"
          class="orders-list"
          v-loading="loading"
          :element-loading-text="t('purchase.loading')"
          @scroll="handleScroll"
        >
          <div v-if="!loading && orders.length === 0" class="empty-state">
            <el-empty :description="t('purchase.noData')"></el-empty>
          </div>

          <div v-for="order in orders" :key="order.id" class="order-card">
            <div class="order-header">
              <div class="order-time">
                <span class="time-icon">🕰️</span>
                <span class="time-text">{{ t('purchase.orderTime') }}: {{ order.addtime }}</span>
              </div>
              <div class="order-id">
                <span class="id-label">{{ t('purchase.orderId') }}</span>
                <span class="id-value">{{ order.id }}</span>
              </div>
            </div>

            <div class="product-section">
              <div class="product-image">
                <img :src="order.goods_pic" alt="" class="product-img">
              </div>
              <div class="product-info">
                <div class="product-name">{{ order.goods_name }}</div>
              </div>
            </div>

            <div class="financial-section">
              <div class="financial-item">
                <span class="financial-label">{{ t('purchase.totalAmount') }}</span>
                <span class="financial-value">{{ formatAmount(order.num) }}</span>
              </div>
              <div class="financial-item">
                <span class="financial-label">{{ t('purchase.commission') }}</span>
                <span class="financial-value">{{ formatAmount(order.commission) }}</span>
              </div>
              <div class="financial-item total">
                <span class="financial-label">{{ t('purchase.expectedReturn') }}</span>
                <span class="financial-total">{{ formatAmount(parseFloat(order.num) + parseFloat(order.commission)) }}</span>
              </div>
            </div>

            <div class="order-actions">
              <div v-if="order.status === 0" class="action-btn primary" @click="showOrderPopup(order)">
                <span class="btn-text">{{ t('purchase.submitOrder') }}</span>
              </div>
              <div v-if="order.status === 1" class="status-text completed">
                <span class="status-icon">✓</span>
                <span class="status-label">{{ t('purchase.orderCompleted') }}</span>
              </div>
            </div>
          </div>

          <div v-if="!loading && orders.length > 0 && noMore" class="no-more-section">
            <div class="no-more-text">{{ t('purchase.noMore') }}</div>
          </div>
        </el-scrollbar>
      </div>


      <div class="popup-overlay" v-if="showPopup" @click="closePopup">
          <div class="popup-container" style="width: 600px;" @click.stop>
              <div class="popup-close" @click="closePopup"><span class="close-icon">×</span></div>
              <div class="popup-content" v-if="currentOrder">
                  <div class="popup-header"><span class="popup-title">{{ t('purchase.orderSuccess') }}</span></div>
                  <div class="popup-product">
                      <div class="popup-product-image"><img :src="currentOrder.goods_pic" alt="" class="product-thumbnail"></div>
                      <div class="popup-product-info">
                          <div class="popup-product-name">{{ currentOrder.goods_name }}</div>
                      </div>
                  </div>
                  <div class="popup-details">
                      <div class="detail-row"><span class="detail-label">{{ t('purchase.orderTime') }}</span><span class="detail-value">{{ currentOrder.addtime }}</span></div>
                      <div class="detail-row"><span class="detail-label">{{ t('purchase.orderNumber') }}</span><span class="detail-value">{{ currentOrder.id }}</span></div>
                      <div class="detail-row"><span class="detail-label">{{ t('purchase.totalAmount') }}</span><span class="detail-value">${{ formatAmount(currentOrder.num) }}</span></div>
                      <div class="detail-row"><span class="detail-label">{{ t('purchase.commission') }}</span><span class="detail-value">${{ formatAmount(currentOrder.commission) }}</span></div>
                      <div class="detail-row total-row"><span class="detail-label">{{ t('purchase.expectedReturn') }}</span><span class="detail-total">${{ formatAmount(parseFloat(currentOrder.num) + parseFloat(currentOrder.commission)) }}</span></div>
                  </div>
                  <div class="popup-rating">
                    <span class="rating-label">{{ t('purchase.rating') }}</span>
                    <el-rate
                      v-model="currentRating"
                      :max="5"
                      :colors="['#FF9900', '#FF9900', '#FF9900']"
                      :void-color="'#E4E7ED'"
                      @change="handleRatingChange"
                    />
                  </div>
                  <div class="popup-actions">
                      <div class="popup-btn cancel" @click="closePopup"><span class="btn-text">{{ t('purchase.notSubmit') }}</span></div>
                      <div class="popup-btn confirm" @click="confirmOrder" :class="{ 'btn-disabled': submitLoading }">
                        <span class="btn-text">{{ submitLoading ? t('purchase.submitting') : t('purchase.submitNow') }}</span>
                      </div>
                  </div>
              </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTransition } from '@vueuse/core'
import { getOrderList, getHomeData, doOrder } from '@/api'
import { ElMessage } from 'element-plus'
import '@/assets/css/purchase.css'

const { t } = useI18n()

const activeTab = ref('all')
const loading = ref(false)
const noMore = ref(false)
const currentPage = ref(1)
const pageSize = 10
const scrollbarRef = ref(null)
const orders = ref([])
const showPopup = ref(false)
const currentOrder = ref(null)
const currentRating = ref(5)
const submitLoading = ref(false)

const tabs = computed(() => [
  { key: 'all', label: t('purchase.all') },
  { key: 'pending', label: t('purchase.pending') },
  { key: 'completed', label: t('purchase.completed') },
  { key: 'frozen', label: t('purchase.frozen') }
])

const fetchOrders = async (tabKey, page = 1, isLoadMore = false) => {
  if (!isLoadMore) {
    orders.value = []
    currentPage.value = 1
    noMore.value = false
  }

  loading.value = true

  try {
    const statusMap = {
      all: 0,
      pending: 1,
      completed: 2,
      frozen: 3
    }

    const response = await getOrderList({
      status: statusMap[tabKey] || 0,
      page,
      num: pageSize
    })

    const result = response.data
    if (result.code === 200) {
      const list = result.data?.list || []

      if (isLoadMore) {
        orders.value.push(...list)
        currentPage.value = page
      } else {
        orders.value = list
      }

      noMore.value = list.length < pageSize
    } else {
      if (!isLoadMore) {
        orders.value = []
      }
    }
  } catch (error) {
    console.error('获取订单失败:', error)
    if (!isLoadMore) {
      orders.value = []
    }
  } finally {
    loading.value = false
  }
}

const switchTab = (key) => {
  if (activeTab.value === key) return
  activeTab.value = key
  fetchOrders(key, 1, false)
  scrollbarRef.value?.setScrollTop(0)
}

const handleScroll = ({ scrollTop }) => {
  const wrap = scrollbarRef.value?.wrapRef
  if (!wrap) return

  const { scrollHeight, clientHeight } = wrap
  const distance = scrollHeight - scrollTop - clientHeight

  if (distance < 50 && !loading.value && !noMore.value) {
    loadMore()
  }
}

const loadMore = () => {
  fetchOrders(activeTab.value, currentPage.value + 1, true)
}

const balance = ref(0)

const animatedBalance = useTransition(balance, {
  duration: 800,
  initialValue: 0
})

const displayBalance = computed(() => {
  const val = animatedBalance.value
  return Number.isFinite(val) ? val.toFixed(2) : '0.00'
})

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  
  const date = new Date(timestamp * 1000)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const formatAmount = (value) => {
  if (!value) return '0.00'
  const num = parseFloat(value)
  return Number.isFinite(num) ? num.toFixed(2) : '0.00'
}

onMounted(async () => {
  try {
    const response = await getHomeData()
    if (response.data.code === 200) {
      const balanceValue = response.data.data.userinfo.balance
      balance.value = parseFloat(balanceValue) || 0
    }
  } catch (error) {
    console.error('获取余额失败:', error)
    balance.value = 0
  }
  fetchOrders('all')
})

const showOrderPopup = (order) => {
  currentOrder.value = order
  showPopup.value = true
}

const closePopup = () => {
  showPopup.value = false
  currentOrder.value = null
}

const confirmOrder = async () => {
  if (!currentOrder.value || submitLoading.value) return
  
  submitLoading.value = true
  
  try {
    const response = await doOrder({
      oid: currentOrder.value.id,
      rating: currentRating.value
    })
    
    if (response.data.code === 200) {
      ElMessage.success(response.data.msg || t('purchase.orderSuccess'))
      closePopup()
      fetchOrders(activeTab.value, 1, false)
    } else {
      ElMessage.error(response.data.msg || t('purchase.submitFailed'))
    }
  } catch (error) {
    console.error('提交订单失败:', error)
    ElMessage.error(t('purchase.submitFailed'))
  } finally {
    submitLoading.value = false
  }
}

const handleRatingChange = (rating) => {
  console.log('用户评分:', rating)
  // 这里可以添加评分处理的逻辑
}
</script>

<style scoped>
.page-wrapper {
  height: 100vh;
  overflow: hidden;
  touch-action: none;
  overscroll-behavior: none;
}

.purchase-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header-section,
.category-section {
  flex-shrink: 0;
}

.orders-section {
  flex: 1;
  overflow: hidden;
}

.orders-list {
  height: calc(100% - 65px);
  position: relative;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.no-more-section {
  padding: 16px;
  text-align: center;
  color: #999;
}

.el-loading-mask {
  display: flex !important;
}

:deep(.el-scrollbar__bar) {
  display: none;
}

.header-section{
    padding: 8px 16px
}

.header-section .balance-card{
    background: linear-gradient(135deg,#667eea,#764ba2);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 5px 15px #667eea4d
}

.header-section .balance-card .balance-content{
    display: flex;
    justify-content: space-between;
    align-items: center
}

.header-section .balance-card .balance-content .balance-label{
    display: flex;
    align-items: center;
    gap: 6px
}

.header-section .balance-card .balance-content .balance-label .balance-icon{
    font-size: 16px
}

.header-section .balance-card .balance-content .balance-label .balance-text{
    font-size: 12px;
    color: #ffffffe6
}

.header-section .balance-card .balance-content .balance-amount{
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0,0,0,.1)
}

@media (max-width: 768px) {
    .purchase-container{
        padding-bottom:70px
    }

    .header-section{
        padding: 10px 12px
    }

    .header-section .balance-card{
        border-radius: 8px;
        padding: 12px 14px
    }

    .header-section .balance-card .balance-content .balance-label{
        gap: 4px
    }

    .header-section .balance-card .balance-content .balance-label .balance-icon{
        font-size: 16px
    }

    .header-section .balance-card .balance-content .balance-label .balance-text{
        font-size: 12px
    }

    .header-section .balance-card .balance-content .balance-amount{
        font-size: 20px
    }

    .category-section{
        padding: 0 12px 8px
    }

    .category-section .category-tabs{
        border-radius: 8px;
        padding: 2px
    }

    .category-section .category-tabs .category-tab{
        padding: 6px 4px;
        border-radius: 4px
    }

    .category-section .category-tabs .category-tab .tab-text{
        font-size: 11px
    }

    .orders-section{
        padding: 0 12px
    }

    .orders-section .order-card{
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 10px
    }

    .orders-section .order-card .order-header{
        margin-bottom: 10px;
        padding-bottom: 8px
    }

    .orders-section .order-card .order-header .order-time{
        gap: 3px
    }

    .orders-section .order-card .order-header .order-time .time-icon{
        font-size: 12px
    }

    .orders-section .order-card .order-header .order-time .time-text{
        font-size: 10px
    }

    .orders-section .order-card .order-header .order-id{
        gap: 3px
    }

    .orders-section .order-card .order-header .order-id .id-label,.orders-section .order-card .order-header .order-id .id-value{
        font-size: 10px
    }

    .orders-section .order-card .product-section{
        gap: 8px;
        margin-bottom: 10px
    }

    .orders-section .order-card .product-section .product-image{
        width: 40px;
        height: 40px;
        border-radius: 4px
    }

    .orders-section .order-card .product-section .product-info .product-name{
        font-size: 12px;
        line-height: 1.3
    }

    .orders-section .order-card .financial-section{
        border-radius: 4px;
        padding: 8px;
        margin-bottom: 10px
    }

    .orders-section .order-card .financial-section .financial-item{
        margin-bottom: 6px
    }

    .orders-section .order-card .financial-section .financial-item .financial-label{
        font-size: 11px
    }

    .orders-section .order-card .financial-section .financial-item .financial-value{
        font-size: 12px
    }

    .orders-section .order-card .financial-section .financial-item.total{
        padding-top: 6px
    }

    .orders-section .order-card .financial-section .financial-item.total .financial-total{
        font-size: 13px
    }

    .orders-section .order-card .order-actions{
        gap: 8px
    }

    .orders-section .order-card .order-actions .action-btn{
        padding: 5px 12px;
        border-radius: 12px
    }

    .orders-section .order-card .order-actions .action-btn .btn-text{
        font-size: 11px
    }

    .orders-section .order-card .order-actions .status-text{
        padding: 5px 10px;
        border-radius: 12px
    }

    .orders-section .order-card .order-actions .status-text.completed .status-icon{
        font-size: 12px
    }

    .orders-section .order-card .order-actions .status-text.completed .status-label{
        font-size: 11px
    }

    .orders-section .loading-section{
        padding: 20px
    }

    .orders-section .loading-section .loading-text{
        font-size: 12px
    }

    .orders-section .no-more-section{
        padding: 15px
    }

    .orders-section .no-more-section .no-more-text{
        font-size: 11px
    }

    .orders-section .empty-section{
        padding: 40px 15px
    }

    .orders-section .empty-section .empty-text{
        font-size: 12px
    }

    .popup-content {
        padding: 16px
    }

    .popup-content .popup-header {
        margin-bottom: 16px;
    }

    .popup-content .popup-header .popup-title {
        font-size: 16px
    }

    .popup-content .popup-product {
        padding: 12px;
        gap: 12px;
        margin-bottom: 16px
    }

    .popup-content .popup-product .popup-product-image {
        width: 50px;
        height: 50px
    }

    .popup-content .popup-product .popup-product-info .popup-product-name {
        font-size: 13px
    }

    .popup-content .popup-details {
        margin-bottom: 16px
    }

    .popup-content .popup-details .detail-row {
        padding: 8px 0
    }

    .popup-content .popup-details .detail-row .detail-label {
        font-size: 12px
    }

    .popup-content .popup-details .detail-row .detail-value {
        font-size: 13px
    }

    .popup-content .popup-details .detail-row.total-row .detail-total {
        font-size: 14px
    }

    .popup-content .popup-rating {
        gap: 12px;
        margin-bottom: 16px
    }

    .popup-content .popup-rating .rating-label {
        font-size: 12px
    }

    .popup-content .popup-rating .rating-stars .star {
        font-size: 18px
    }

    .popup-content .popup-actions {
        gap: 12px
    }

    .popup-content .popup-actions .popup-btn {
        padding: 10px
    }

    .popup-content .popup-actions .popup-btn .btn-text {
        font-size: 13px
    }
}

.popup-overlay {
    position: fixed;
    inset: 0;
    background-color: #000000b3;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999
}

.popup-container {
    background: #fff;
    border-radius: 16px;
    padding: 0;
    position: relative;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 30px #0000004d
}

.popup-close {
    position: absolute;
    top: -40px;
    right: 50%;
    transform: translate(50%);
    width: 28px;
    height: 28px;
    border: 1px solid rgba(255,255,255,.8);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0000004d;
    backdrop-filter: blur(5px);
    cursor: pointer;
    transition: all .3s ease
}

.popup-close:hover {
    background: #00000080;
    transform: translate(50%) scale(1.1)
}

.popup-close .close-icon {
    color: #fff;
    font-size: 20px;
    line-height: 1
}

.popup-enter-active,.popup-leave-active {
    transition: all .3s ease
}

.popup-enter-from,.popup-leave-to {
    opacity: 0
}

.popup-enter-from .popup-container,.popup-leave-to .popup-container {
    transform: scale(.9)
}

.popup-content .popup-actions .popup-btn.confirm.btn-disabled {
    opacity: .6;
    cursor: not-allowed;
    background: #999
}
</style>
